<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { usePendingMediaRecovery } from 'dashboard/composables/usePendingMediaRecovery';

const { contentAttributes, id, conversationId, createdAt } =
  useMessageContext();

const now = ref(Date.now());
let clock = null;
onMounted(() => {
  clock = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => clearInterval(clock));

const waitingLabel = computed(() => {
  const created = Number(createdAt?.value);
  if (!created) return 'Baixando mídia…';
  const seconds = Math.max(0, Math.floor(now.value / 1000 - created));
  if (seconds < 60) return `Baixando mídia… ${seconds}s`;
  return `Baixando mídia… ${Math.floor(seconds / 60)}min ${seconds % 60}s`;
});

const attrs = computed(() => contentAttributes.value || {});

const stage = computed(() =>
  String(
    attrs.value.wameMediaStage ?? attrs.value.wame_media_stage ?? 'bank'
  ).toLowerCase()
);

const isComplete = computed(() => stage.value === 'complete');

// Aceleres: a midia do WhatsApp vive num link temporario do CDN da Meta. Se a
// WAME nao consegue busca-la enquanto o link existe, o arquivo e perdido de
// vez — e o Laravel marca o estagio `failed` depois de esgotar as rodadas de
// reparo. Antes disso a bolha prometia "Entregando no atendimento" para
// sempre: o atendente ficava esperando um anexo que nunca chegaria.
const isFailed = computed(() => stage.value === 'failed');

// Aceleres: falhar nao basta, e preciso dizer POR QUE — as duas causas pedem
// acoes opostas do atendente. `expired` e o arquivo que saiu do ar no CDN da
// Meta: pedir reenvio ao cliente resolve. `connection` e a conexao que parou
// de entregar arquivo nenhum (28/08/2026: a WAME respondia "Message not
// found" ate para a midia de uma mensagem que ela mesma acabara de enviar);
// ali pedir reenvio so produz outra midia perdida, porque o proximo arquivo
// se perde igual ate alguem reconectar. A bolha afirmava "expirou no
// WhatsApp" nos dois casos, e a causa errada mandou a investigacao para o
// lado errado durante horas.
const isConnectionFailure = computed(
  () =>
    String(
      attrs.value.wameMediaFailedCause ?? attrs.value.wame_media_failed_cause ?? ''
    ).toLowerCase() === 'connection'
);

const fileName = computed(() =>
  String(
    attrs.value.wameMediaFileName ?? attrs.value.wame_media_file_name ?? ''
  ).trim()
);

const mediaLabel = computed(() => {
  const label = String(
    attrs.value.wameMediaLabel ?? attrs.value.wame_media_label ?? ''
  )
    .replace(/^\[|\]$/g, '')
    .trim();

  return label || 'Mídia';
});

const title = computed(() => {
  if (!isFailed.value) return 'Mídia recebida';

  return fileName.value || mediaLabel.value;
});

const currentLabel = computed(() => {
  if (isFailed.value) {
    return isConnectionFailure.value
      ? 'A conexão do WhatsApp não entregou o arquivo'
      : 'Não foi possível baixar do WhatsApp';
  }

  return isComplete.value ? 'Mídia disponível' : 'Entregando no atendimento';
});

const failureHint = computed(() =>
  isConnectionFailure.value
    ? 'Falha da conexão, não do cliente. Enquanto ela não for reconectada, as próximas mídias também não chegam — avise quem cuida dela antes de pedir o reenvio.'
    : 'O arquivo expirou no WhatsApp antes de ser transferido. Peça ao cliente para reenviar.'
);

const icon = computed(() => {
  if (isFailed.value) return 'i-lucide-file-x';

  return isComplete.value ? 'i-lucide-check' : 'i-lucide-paperclip';
});

// Aceleres 2026-09-09: enquanto este cartao estiver no ecra, a bolha pergunta
// pela propria mensagem de tempos a tempos. E a rede de seguranca para quem NAO
// e o responsavel pela conversa: o broadcast `message.updated` do Zoraxy so vai
// para o responsavel, e sem isto um gestor a acompanhar ficava aqui preso ate
// dar F5. Nao vale a pena perguntar por uma midia ja declarada perdida.
usePendingMediaRecovery(
  conversationId,
  id,
  computed(() => !isFailed.value)
);
</script>

<template>
  <BaseBubble
    data-bubble-name="wame-media-progress"
    class="min-w-[220px] border px-3 py-2.5"
    :class="
      isFailed
        ? 'border-[#e8d8d4] bg-[#faf1ef] text-[#7a4b3f]'
        : 'border-[#d5e6df] bg-[#effaf4] text-[#285247]'
    "
  >
    <div class="flex items-center gap-2.5">
      <span
        class="inline-flex size-[30px] shrink-0 items-center justify-center rounded-full bg-white/70 ring-1 ring-inset"
        :class="
          isFailed
            ? 'text-[#b4593f] ring-[#b4593f]/10'
            : [
                'text-[#2f8f70] ring-[#2f8f70]/10',
                isComplete ? 'bg-[#3a9b78] text-white' : '',
              ]
        "
      >
        <Icon :icon="icon" class="size-4" />
      </span>
      <div class="min-w-0">
        <p class="text-sm font-medium leading-5 truncate">{{ title }}</p>
        <p class="text-xs leading-4 opacity-70">{{ currentLabel }}</p>
      </div>
    </div>

    <!-- Nao ha dado de progresso real (o estagio e so bank/complete/failed):
    a antiga faixa "Recebida no banco / Atendimento" parecia uma barra que
    nunca andava. Indicador indeterminado + tempo de espera. -->
    <div
      v-if="!isFailed && !isComplete"
      class="mt-2 flex items-center gap-2 text-[11px] leading-4 opacity-70"
    >
      <span
        class="inline-block size-3 shrink-0 animate-spin rounded-full border-2 border-[#2f8f70]/25 border-t-[#2f8f70]"
      />
      <span>{{ waitingLabel }}</span>
    </div>
    <p v-else class="mt-1.5 break-words text-[11px] leading-4 opacity-70">
      {{ failureHint }}
    </p>
  </BaseBubble>
</template>
